/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { RefObject, useEffect } from "react";

const useGooglePlaces = (
  addressInputRef: RefObject<HTMLInputElement>,
  onSelect: (data: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  }) => void,
) => {
  useEffect(() => {
    let autocomplete = null;

    // Load Google Maps API script dynamically
    const loadGoogleMapsScript = () => {
      return new Promise<void>((resolve, reject) => {
        // Check if already loaded
        if (typeof window.google !== "undefined" && window.google.maps && window.google.maps.places) {
          resolve();
          return;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          // Wait for it to load
          existingScript.addEventListener("load", () => resolve());
          existingScript.addEventListener("error", reject);
          return;
        }

        const apiKey = process.env.GOOGLE_MAPS_API_KEY;
        if (!apiKey) {
          console.warn("Google Places API key not configured. Autocomplete will not be available.");
          reject(new Error("API key not configured"));
          return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.addEventListener("load", () => resolve());
        script.addEventListener("error", reject);
        document.head.appendChild(script);
      });
    };

    const initAutocomplete = async () => {
      try {
        await loadGoogleMapsScript();

        if (addressInputRef.current) {
          autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
            types: ["address"],
            componentRestrictions: { country: ["us", "ca"] }, // Restrict to US and Canada
          }) as object;

          // Listen for when a place is selected
          autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();

            if (!place.address_components) {
              return;
            }

            // Extract address components
            let streetNumber = "";
            let route = "";
            let address2 = "";
            let city = "";
            let state = "";
            let zipCode = "";
            let country = "";

            place.address_components.forEach((component) => {
              const types = component.types;

              if (types.includes("subpremise")) {
                address2 = component.long_name;
              }

              if (types.includes("street_number")) {
                streetNumber = component.long_name;
              }
              if (types.includes("route")) {
                route = component.long_name;
              }
              if (types.includes("locality")) {
                city = component.long_name;
              }
              if (types.includes("administrative_area_level_1")) {
                state = component.short_name; // Use short_name for state abbreviation
              }
              if (types.includes("postal_code")) {
                zipCode = component.long_name;
              }
              if (types.includes("country")) {
                country = component.long_name;
              }
            });

            // Update form data with extracted address components
            onSelect({
              address: `${streetNumber} ${route}`.trim(),
              address2,
              city,
              state,
              zipCode,
              country,
            });
          });
        }
      } catch (error) {
        console.warn(
          "Google Places API could not be loaded:",
          error instanceof Error ? error.message : String(error),
        );
      }
    };

    // Initialize autocomplete
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    initAutocomplete();

    return () => {
      if (autocomplete && window.google?.maps?.event) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, []);
};

export default useGooglePlaces;
